import * as THREE from 'three';
import { FOG_FRAG, FOG_VERT, fxUniforms } from './common';

/**
 * Ancient magic orb (instanced): a thick glass sphere holding a living element.
 *
 * Per instance (same layout as the old gem material, so the relic code is unchanged):
 *  iColor = element colour, iGem = (glow, grade 1..5, flash, seed), iDetail = (element: 0..8 the first spheres, 9–10 totems, 11 obsidian, 12 storm eye; focus).
 *
 * The interior is a short ray march (3–4 steps) plus large refracted forms along the view ray,
 * with one recipe per element:
 *  - ember (0): a molten core with flame tendrils rising and swirling, embers flickering in it
 *  - frost (1): a frozen heart crossed by crystalline veins, slow snow haze drifting inside
 *  - sun   (2): a radiant plasma core with filaments crawling over it and arcs on the inner wall
 *  - venom (3): murky toxin filling most of the sphere, a swirling current and rising bubbles
 *  - arcane/prism/blood/time/gravity (4..8): spiral, spectral facets, heart, dial and a dark horizon
 * Grade deepens the volume and adds readable branches, rays and facets without bleaching the core;
 * grade III adds a quiet etched rune band. The shell adds fresnel, a key-light highlight and
 * a cool environment reflection. Output is linear HDR.
 */
export function createOrbMaterial(options: { volumeSteps?: 3 | 4; satellite?: boolean } = {}): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    defines: { ORB_STEPS: options.volumeSteps ?? 4, ...(options.satellite ? { SATELLITE: 1 } : {}) },
    uniforms: {
      uTime: fxUniforms.uTime,
      uFogDensity: fxUniforms.uFogDensity,
      uLightDir: { value: new THREE.Vector3(-6, 13, 7).normalize() },
    },
    vertexShader: /* glsl */ `
      attribute vec3 iColor;
      attribute vec4 iGem;
      attribute vec2 iDetail;
      varying vec3 vObj, vRay, vWorldN, vWorld, vColor;
      varying vec4 vGem; varying vec2 vDetail;
      ${FOG_VERT}
      void main() {
        #ifdef USE_INSTANCING
          mat4 m = modelMatrix * instanceMatrix;
        #else
          mat4 m = modelMatrix;
        #endif
        vec4 world = m * vec4(position, 1.0);
        vWorld = world.xyz;
        vObj = normalize(position);
        mat3 r = mat3(m);
        // object-space view ray (the matrix is rotation + uniform scale, so its transpose inverts the turn)
        vRay = normalize(transpose(r) * (world.xyz - cameraPosition));
        vWorldN = normalize(r * normal);
        vColor = iColor; vGem = iGem; vDetail = iDetail;
        vec4 mv = viewMatrix * world;
        FOG_FROM_MV(mv)
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uLightDir;
      varying vec3 vObj, vRay, vWorldN, vWorld, vColor;
      varying vec4 vGem; varying vec2 vDetail;
      ${FOG_FRAG}

      float h31(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
      float n3(vec3 x) {
        vec3 i = floor(x), f = fract(x); f = f * f * (3.0 - 2.0 * f);
        return mix(mix(mix(h31(i), h31(i + vec3(1, 0, 0)), f.x), mix(h31(i + vec3(0, 1, 0)), h31(i + vec3(1, 1, 0)), f.x), f.y),
                   mix(mix(h31(i + vec3(0, 0, 1)), h31(i + vec3(1, 0, 1)), f.x), mix(h31(i + vec3(0, 1, 1)), h31(i + vec3(1, 1, 1)), f.x), f.y), f.z);
      }
      vec3 swirl(vec3 p, float a) { float c = cos(a), s = sin(a); return vec3(c * p.x - s * p.z, p.y, s * p.x + c * p.z); }
      float stroke(float d, float w) { return 1.0 - smoothstep(w, w + max(fwidth(d), 0.008), abs(d)); }
      float segment(vec2 p, vec2 a, vec2 b, float w) {
        vec2 v = b - a; float h = clamp(dot(p - a, v) / dot(v, v), 0.0, 1.0);
        float d = length(p - a - v * h); return 1.0 - smoothstep(w, w + max(fwidth(d), 0.008), d);
      }

      void main() {
        float glow = vGem.x, grade = vGem.y, flash = vGem.z, seed = vGem.w, g01 = (grade - 1.0) / 4.0;
        float el = vDetail.x, focus = vDetail.y;
        vec3 col = vColor;
        float t = uTime * (0.45 + 0.18 * grade) + seed * 40.0;
        #ifdef SATELLITE
          // At 2–6 px an orbiting mote cannot resolve a volume, glyphs or glass wear.
          float face = max(0.0, dot(normalize(vWorldN), normalize(cameraPosition - vWorld)));
          vec3 tint = el > 4.5 && el < 5.5 ? 0.5 + 0.5 * cos(seed + uTime * 0.4 + vec3(0.0, 2.09, 4.19)) : col;
          gl_FragColor = vec4((tint * (0.22 + face * 0.75) * glow + tint * flash * 0.25) * fogKeep(), 1.0);
          return;
        #endif

        // ---- interior: march the refracted ray from the entry point to the far wall
        vec3 ro = normalize(vObj), rd = normalize(refract(normalize(vRay), ro, 1.0 / 1.45));
        float chord = max(0.0, -2.0 * dot(ro, rd));
        vec3 acc = vec3(0.0); float trans = 1.0;
        for (int i = 0; i < ORB_STEPS; i++) {
          vec3 p = ro + rd * chord * (float(i) + 0.5) / float(ORB_STEPS);
          float r = length(p);
          vec3 e = vec3(0.0); float dens = 0.0;
          if (el < 0.5) {
            // ember: tendrils rise and twist around a molten heart
            vec3 q = swirl(p, t * 0.6 + p.y * 2.2) * 3.4 + vec3(0.0, -t * 1.8, 0.0);
            float n = n3(q) * 0.6 + n3(q * 2.3 + 4.0) * 0.4;
            float core = exp(-r * r * (9.0 - 3.0 * g01));
            float tongue = smoothstep(0.56 - 0.05 * g01, 0.74, n) * (1.0 - r * 0.6);
            dens = tongue * 1.4 + core * 0.8 + 0.35;
            // a deep crimson body, thin flame tongues running orange to yellow, a white-gold heart
            e = vec3(0.16, 0.015, 0.004) * (1.0 - r * 0.5)
              + mix(vec3(1.0, 0.16, 0.02), vec3(1.0, 0.58, 0.1), smoothstep(0.4, 1.0, tongue)) * tongue * 2.1
              + vec3(1.0, 0.72, 0.35) * core * (0.3 + 0.45 * g01);
          } else if (el < 1.5) {
            // frost: crystalline veins (noise ridges) through a cold glow, drifting snow haze
            vec3 q = p * (2.6 + g01) + seed * 7.0;
            float vein = 1.0 - abs(n3(q) - 0.5) * 2.0;
            vein = pow(vein, 14.0 - 4.0 * g01);
            float haze = n3(p * 3.0 + vec3(t * 0.15, -t * 0.25, 0.0));
            float core = exp(-r * r * 4.0);
            dens = vein * 0.9 + haze * 0.18 + core * 0.35;
            e = col * (haze * 0.12 + core * 0.3) + mix(col, vec3(0.85, 0.95, 1.0), 0.45) * vein * (0.8 + 0.5 * g01);
          } else if (el < 2.5) {
            // sun: a white-gold core, filaments crawling over it
            vec3 q = swirl(p, t * 0.9) * 3.0 + vec3(0.0, t * 0.6, 0.0);
            float fil = pow(1.0 - abs(n3(q) - 0.5) * 2.0, 10.0);
            float core = exp(-r * r * (14.0 - 5.0 * g01));
            dens = core * 1.3 + fil * (0.6 + 0.4 * g01);
            e = vec3(1.0, 0.88, 0.55) * core * (0.9 + 0.6 * g01) + col * fil * (0.9 + 0.6 * g01) + col * 0.05;
          } else if (el > 11.5) {
            // storm eye: a dark cloud wheeling round a calm bright eye, lightning flickering in the bands
            vec3 q = swirl(p, t * (1.3 + g01) + 2.2 / max(r, 0.2));
            float cloud = n3(vec3(atan(q.z, q.x) * 2.0, r * 5.0, t * 0.4)) * smoothstep(0.18, 0.4, r);
            float eye = exp(-r * r * (30.0 - 8.0 * g01));
            float bolt = pow(1.0 - abs(n3(q * 4.0 + floor(t * 3.0)) - 0.5) * 2.0, 24.0) * step(0.6, h31(vec3(floor(t * 3.0), seed, 1.0)));
            dens = cloud * 0.9 + eye * 1.2 + bolt * 0.8 + 0.06;
            e = col * 0.06 * cloud + mix(col, vec3(1.0), 0.6) * eye * (0.9 + 0.5 * g01) + vec3(0.85, 0.97, 1.0) * bolt * 2.4;
          } else if (el > 10.5) {
            // obsidian: black volcanic glass, thin molten fissures breathing in it
            vec3 q = p * (2.4 + g01) + seed * 5.0;
            float crack = pow(1.0 - abs(n3(q + vec3(0.0, t * 0.08, 0.0)) - 0.5) * 2.0, 22.0 - 6.0 * g01);
            float ember = exp(-r * r * 10.0) * (0.5 + 0.5 * sin(t * 1.6));
            dens = 1.1 + crack * 0.6;
            e = vec3(0.012, 0.01, 0.014) + col * crack * (1.4 + 0.8 * g01) + col * ember * 0.25;
          } else if (el > 9.5) {
            // ascension (totem): golden motes spiralling upward round a soft white-gold column
            vec3 q = swirl(p, t * 1.4 + p.y * 3.0);
            float spiral = pow(max(0.0, sin(atan(q.z, q.x) * 3.0 + p.y * 9.0 - t * 4.0)), 8.0) * smoothstep(0.75, 0.3, length(p.xz));
            float column = exp(-dot(p.xz, p.xz) * 18.0);
            dens = spiral * 0.8 + column * 0.9 + 0.04;
            e = mix(col, vec3(1.0, 0.95, 0.8), 0.4) * spiral * 1.4 + vec3(1.0, 0.9, 0.62) * column * 0.9;
          } else if (el > 8.5) {
            // wind (totem): pale gusts streaming round the sphere in banded swirls, a clear calm eye
            vec3 q = swirl(p, t * 1.1 + r * 4.0);
            float band = pow(1.0 - abs(n3(vec3(atan(q.z, q.x) * 1.6, q.y * 5.0, t * 0.6)) - 0.5) * 2.0, 6.0);
            float gust = smoothstep(0.25, 0.85, r) * band;
            dens = gust * 0.7 + 0.03;
            e = mix(col, vec3(0.9, 1.0, 0.98), 0.35) * gust * 1.25 + col * 0.04;
          } else if (el > 7.5) {
            // gravity: a black core swallowing light, a pink accretion disc wheeling round it, lensed streaks
            vec3 q = swirl(p, t * (1.2 + g01) + 1.5 / max(r, 0.15));
            float disc = exp(-q.y * q.y * 40.0) * smoothstep(0.18, 0.32, r) * (1.0 - smoothstep(0.75, 0.95, r));
            float streak = pow(n3(vec3(atan(q.z, q.x) * 3.0, r * 6.0, t * 0.5)), 3.0);
            float hole = 1.0 - smoothstep(0.14, 0.22, r);
            float halo = exp(-pow((r - 0.24) * 18.0, 2.0));
            dens = disc * (0.6 + streak) + hole * 3.0 + 0.05;
            e = mix(col, vec3(1.0, 0.85, 0.95), smoothstep(0.5, 0.25, r)) * disc * (0.6 + streak * 1.6) * (1.0 + g01 * 0.6)
              + mix(col, vec3(1.0), 0.5) * halo * (0.8 + 0.6 * g01);
          } else if (el > 6.5) {
            // The dial is intersected once below; repeating it at every volume sample bleaches the hands.
            float dust = pow(n3(p * 5.0 + vec3(0.0, -t * 0.2, 0.0)), 8.0) * (1.0 - r);
            dens = 0.06;
            e = vec3(0.35, 0.19, 0.05) * dust * 0.25;
          } else if (el > 5.5) {
            // blood: a double-beating garnet heart with dark wine chambers and crimson veins.
            float bt = uTime * 4.0 + seed * 23.0;
            float beat = pow(max(0.0, sin(bt)), 12.0) + 0.55 * pow(max(0.0, sin(bt - 0.75)), 18.0);
            vec3 q = p / (1.0 + beat * 0.09 + min(flash, 1.0) * 0.04);
            float lobes = exp(-dot(q - vec3(-0.19, 0.12, 0.0), q - vec3(-0.19, 0.12, 0.0)) * 22.0)
                        + exp(-dot(q - vec3(0.19, 0.12, 0.0), q - vec3(0.19, 0.12, 0.0)) * 22.0);
            float root = exp(-dot(q - vec3(0.0, -0.19, 0.0), q - vec3(0.0, -0.19, 0.0)) * 17.0);
            float veins = pow(1.0 - abs(n3(swirl(q, q.y * 1.8) * 3.5 + seed) - 0.5) * 2.0, 18.0) * (1.0 - r * 0.8);
            float heart = lobes * 0.65 + root * 0.5;
            dens = 0.18 + heart * 0.9 + veins * 0.55;
            e = vec3(0.21, 0.005, 0.035) * (0.5 + heart)
              + vec3(0.95, 0.025, 0.16) * heart * (0.6 + beat * 0.65)
              + vec3(1.0, 0.12, 0.33) * veins * (0.75 + g01 * 0.6 + beat * 0.3);
          } else if (el > 4.5) {
            // prism: almost clear glass; thin rainbow caustics drift through it and white glints flicker
            vec3 q = swirl(p, t * 0.25) * (3.0 + g01);
            float ridge = pow(1.0 - abs(n3(q) - 0.5) * 2.0, 18.0);
            float hue = dot(p, vec3(0.6, 0.8, 0.3)) * 1.4 + t * 0.15;
            vec3 rainbow = 0.5 + 0.5 * cos(6.2832 * (hue + vec3(0.0, 0.33, 0.67)));
            vec3 sc = floor(p * 6.0);
            float glint = step(0.92 - 0.04 * g01, h31(sc + floor(t * 2.0) + seed)) * smoothstep(0.3, 0.0, length(fract(p * 6.0) - 0.5));
            dens = ridge * 0.4 + glint * 0.5 + 0.03;
            e = rainbow * ridge * (1.1 + 0.6 * g01) + vec3(1.0) * glint * 1.5 + vec3(0.06, 0.07, 0.09);
          } else if (el > 3.5) {
            // arcane: a violet nebula folding over itself, pin-prick stars flaring in it
            vec3 q = swirl(p, t * 0.5 + r * 2.5) * 2.6;
            float cloud = n3(q + vec3(0.0, t * 0.2, 0.0)) * 0.6 + n3(q * 2.2 - t * 0.3) * 0.4;
            float neb = smoothstep(0.42, 0.85, cloud);
            vec3 sc = floor(p * 7.0);
            float star = step(0.9 - 0.05 * g01, h31(sc + seed)) * smoothstep(0.25, 0.0, length(fract(p * 7.0) - 0.5)) * (0.5 + 0.5 * sin(t * 6.0 + h31(sc) * 30.0));
            float core = exp(-r * r * 6.0);
            dens = neb * 0.9 + core * 0.4 + 0.1;
            e = mix(col * vec3(0.55, 0.45, 1.0), vec3(1.0, 0.45, 0.95), smoothstep(0.6, 1.0, cloud)) * neb * 1.4
              + vec3(1.0, 0.95, 1.0) * star * 2.0 + vec3(0.9, 0.75, 1.0) * core * (0.3 + 0.4 * g01) + col * 0.05;
          } else {
            // venom: a murky liquid filling the lower part, a slow current and rising bubbles
            float level = 0.35 + 0.08 * sin(t * 0.7 + p.x * 3.0) * 0.4;
            float liquid = smoothstep(level + 0.04, level - 0.04, p.y);
            vec3 q = swirl(p, t * 0.5) * 2.4 + vec3(0.0, t * 0.3, 0.0);
            float cur = n3(q) * 0.7 + n3(q * 2.1) * 0.3;
            vec3 bc = p * 5.0 + vec3(0.0, -t * 1.2, 0.0);
            vec3 cell = floor(bc), f = fract(bc) - 0.5;
            float bub = step(0.83 - 0.06 * g01, h31(cell + seed)) * smoothstep(0.22, 0.12, length(f));
            dens = liquid * (0.35 + cur * 0.5) + bub * liquid * 0.9 + (1.0 - liquid) * 0.06;
            e = col * vec3(0.6, 0.9, 0.5) * liquid * (0.08 + smoothstep(0.45, 0.8, cur) * 0.5) + mix(col, vec3(0.85, 1.0, 0.6), 0.4) * bub * liquid * 1.1 + col * 0.04 * (1.0 - liquid);
          }
          acc += e * trans * (1.5 / float(ORB_STEPS));
          trans *= exp(-dens * (3.0 / float(ORB_STEPS)));
        }
        vec3 inner = acc * min(max(glow, 0.8), 1.45) * (0.68 + 0.14 * g01);
        // Large refracted forms survive the 17 px gameplay view. The volume supplies depth and motion,
        // these derivative-filtered shapes supply identity; they stay inside the glass, with no extra mesh.
        vec3 side = normalize(cross(normalize(vRay), vec3(0.0, 1.0, 0.0)));
        vec3 up = normalize(cross(side, normalize(vRay)));
        vec3 mid = ro + rd * chord * 0.5;
        vec2 q = vec2(dot(mid, side), dot(mid, up)) * 1.08;
        float rad = length(q), angle = atan(q.y, q.x), mark = 0.0;
        // Extra fine branches only where they can occupy a pixel, independent of device DPR.
        float resolved = 1.0 - smoothstep(0.024, 0.042, length(fwidth(q)));
        vec3 ink = col; float hole = 0.0;
        if (el < 0.5) {
          // A broad ember tongue: asymmetric crown and a darker red root.
          float y = q.y + 0.15, width = 0.28 * (1.0 - smoothstep(-0.25, 0.45, q.y));
          float bend = sin(y * 8.0 - uTime * 2.3 + seed * 5.0) * y * 0.085;
          float d = length(vec2((q.x + bend) / max(width, 0.04), (q.y + 0.04) * 2.1));
          mark = 1.0 - smoothstep(0.82, 1.0, d);
          if (grade >= 3.0) {
            float tongue = length(vec2((q.x + 0.14 + bend) * 7.0, (q.y - 0.12) * 3.8));
            mark = max(mark, (1.0 - smoothstep(0.7, 1.0, tongue)) * 0.65);
          }
          ink = mix(vec3(0.8, 0.045, 0.008), vec3(1.0, 0.56, 0.085), smoothstep(-0.3, 0.35, q.y));
        } else if (el < 1.5) {
          // Six substantial frost branches around one angular frozen heart.
          for (int j = 0; j < 3; j++) {
            float a = float(j) * 1.0472 + 0.18;
            vec2 v = vec2(cos(a), sin(a));
            mark = max(mark, segment(q, -v * 0.45, v * 0.45, 0.025));
            if (grade >= 3.0 && resolved > 0.0) {
              vec2 wing = vec2(-v.y, v.x) * 0.13;
              mark = max(mark, segment(q, v * 0.35 + wing, v * 0.2, 0.015) * resolved);
              mark = max(mark, segment(q, -v * 0.35 - wing, -v * 0.2, 0.015) * resolved);
            }
          }
          float diamond = abs(q.x) * 1.3 + abs(q.y) - 0.18;
          mark = max(mark, 1.0 - smoothstep(-0.02, max(fwidth(diamond), 0.01), diamond));
          ink = mix(vec3(0.13, 0.48, 0.86), vec3(0.52, 0.84, 1.0), step(0.0, q.x));
        } else if (el < 2.5) {
          // Four long solar rays and a hot amber heart, instead of a white glowing ball.
          float bound = 0.22 + pow(abs(cos(angle * (grade >= 5.0 ? 4.0 : 2.0) + 0.1)), 8.0) * 0.27;
          mark = 1.0 - smoothstep(bound - 0.025, bound + max(fwidth(rad), 0.01), rad);
          if (grade >= 3.0) mark = max(mark, stroke(rad - 0.42, 0.014) * resolved * 0.35);
          ink = mix(vec3(1.0, 0.48, 0.025), vec3(1.0, 0.85, 0.25), exp(-rad * rad * 28.0));
        } else if (el < 3.5) {
          // A moving meniscus and large hollow bubbles keep toxin distinct from nebulae.
          float surface = q.y + 0.12 - sin(q.x * 6.0 + uTime * 1.0) * 0.035;
          mark = stroke(surface, 0.026) * (1.0 - smoothstep(0.46, 0.54, rad));
          mark = max(mark, stroke(length(q - vec2(-0.17, 0.13 + sin(uTime * 0.8) * 0.035)) - 0.105, 0.024));
          if (grade >= 3.0) mark = max(mark, stroke(length(q - vec2(0.16, -0.26)) - 0.075, 0.02));
          if (grade >= 5.0) mark = max(mark, stroke(length(q - vec2(-0.29, -0.31)) - 0.055, 0.015) * resolved);
          ink = vec3(0.24, 0.84, 0.075);
        } else if (el < 4.5) {
          // Three broad violet spiral arms; the dark gaps remain visible even on rank V.
          float arms = pow(max(0.0, cos(angle * 3.0 + rad * 9.0 - uTime * 0.65)), 5.0);
          mark = arms * smoothstep(0.07, 0.15, rad) * (1.0 - smoothstep(0.43, 0.53, rad));
          mark = max(mark, exp(-rad * rad * 150.0) * 0.5);
          if (grade >= 3.0) {
            vec2 star = q - vec2(cos(uTime * 0.25), sin(uTime * 0.25)) * 0.39;
            mark = max(mark, exp(-dot(star, star) * 450.0) * resolved);
          }
          ink = mix(vec3(0.34, 0.1, 0.85), vec3(0.67, 0.4, 1.0), rad);
        } else if (el < 5.5) {
          // A hollow prism with broad spectral edges and three inner facets.
          float d = abs(q.x) + abs(q.y) * 0.72 - 0.41;
          mark = stroke(d, 0.026);
          mark = max(mark, segment(q, vec2(0.0, 0.56), vec2(0.0, -0.56), 0.018) * 0.55);
          mark = max(mark, segment(q, vec2(-0.41, 0.0), vec2(0.0, -0.56), 0.018) * 0.7);
          vec3 rainbow = 0.5 + 0.5 * cos(angle + uTime * 0.15 + vec3(0.0, 2.09, 4.19));
          ink = rainbow * 0.85 + 0.15;
          if (grade >= 3.0) mark = max(mark, stroke(abs(q.x) + abs(q.y) * 0.72 - 0.29, 0.015) * resolved * 0.45);
          inner *= 0.6;
        } else if (el < 6.5) {
          // One legible two-lobed heart. Its double beat moves inside a steady shell.
          float beat = pow(max(0.0, sin(uTime * 4.0 + seed * 23.0)), 12.0);
          vec2 h = q * 1.35 / (1.0 + beat * 0.08); h.y += 0.015;
          float d = dot(h, h) - 0.13;
          float heart = d * d * d - h.x * h.x * h.y * h.y * h.y;
          float aa = max(fwidth(heart), 0.00005);
          mark = 1.0 - smoothstep(-aa, aa, heart);
          ink = mix(vec3(0.62, 0.008, 0.045), vec3(1.0, 0.045, 0.17), beat * 0.65 + 0.15);
        } else if (el > 11.5) {
          // storm eye: a round eye with a bright pupil and two spiral arms sweeping round it
          float arms = pow(max(0.0, cos(angle * 2.0 - rad * 11.0 + uTime * 1.4)), 6.0) * smoothstep(0.16, 0.22, rad) * (1.0 - smoothstep(0.46, 0.55, rad));
          mark = max(arms * 0.8, stroke(rad - 0.13, 0.022));
          mark = max(mark, 1.0 - smoothstep(0.045, 0.065, rad));
          ink = mix(col, vec3(0.92, 1.0, 1.0), smoothstep(0.3, 0.0, rad));
        } else if (el > 10.5) {
          // obsidian: a target sigil — a ring, four ticks pointing in, a burning dot
          mark = stroke(rad - 0.34, 0.026);
          for (int j = 0; j < 4; j++) {
            float a = float(j) * 1.5708 + 0.785;
            vec2 v = vec2(cos(a), sin(a));
            mark = max(mark, segment(q, v * 0.47, v * 0.22, 0.024));
          }
          mark = max(mark, 1.0 - smoothstep(0.05, 0.07, rad));
          if (grade >= 3.0) mark = max(mark, stroke(rad - 0.17, 0.014) * resolved * 0.6);
          ink = mix(vec3(0.85, 0.2, 0.03), vec3(1.0, 0.62, 0.18), smoothstep(0.4, 0.0, rad));
        } else if (el > 9.5) {
          // ascension: a chevron stack rising toward a small crown point
          for (int j = 0; j < 3; j++) {
            float y = -0.32 + float(j) * 0.22;
            mark = max(mark, segment(q, vec2(-0.2, y - 0.06), vec2(0.0, y + 0.06), 0.026));
            mark = max(mark, segment(q, vec2(0.0, y + 0.06), vec2(0.2, y - 0.06), 0.026));
          }
          mark = max(mark, 1.0 - smoothstep(0.045, 0.06, length(q - vec2(0.0, 0.42))));
          ink = mix(vec3(1.0, 0.7, 0.2), vec3(1.0, 0.95, 0.75), smoothstep(-0.3, 0.4, q.y));
        } else if (el > 8.5) {
          // wind: three curling gust lines, each ending in a little curl
          for (int j = 0; j < 3; j++) {
            float y = 0.2 - float(j) * 0.2, len = 0.38 - float(j) * 0.08;
            mark = max(mark, segment(q, vec2(-0.38, y), vec2(len - 0.1, y), 0.022));
            mark = max(mark, stroke(length(q - vec2(len - 0.1, y + 0.08)) - 0.08, 0.02) * step(q.y, y + 0.08) * step(len - 0.1, q.x + 0.001 + 0.08));
          }
          ink = mix(vec3(0.3, 0.8, 0.78), vec3(0.85, 1.0, 0.98), smoothstep(-0.4, 0.4, q.x));
        } else if (el > 7.5) {
          // The hole must stay black: shell glints and volume light cannot fill its centre.
          hole = 1.0 - smoothstep(0.17, 0.225, rad);
          mark = stroke(rad - 0.255, 0.035) + stroke(length(q * vec2(0.75, 2.4)) - 0.38, 0.028) * 0.5;
          ink = mix(vec3(0.75, 0.11, 0.38), vec3(1.0, 0.48, 0.68), smoothstep(0.24, 0.2, rad));
          inner *= 1.0 - hole;
        }
        mark *= 1.0 - smoothstep(0.58, 0.72, rad);
        inner = inner * (1.0 - clamp(mark, 0.0, 1.0) * 0.5) + ink * mark * (0.65 + 0.22 * g01);
        if (el > 6.5 && el < 7.5 && abs(rd.y) > 0.001) {
          // Intersect the actual dial plane analytically: five volume samples alone can miss thin tick marks.
          float at = -ro.y / rd.y;
          vec3 q = ro + rd * at;
          float rad = length(q.xz), angle = atan(q.z, q.x) + seed * 6.0;
          float on = step(0.0, at) * step(at, chord) * (1.0 - smoothstep(0.71, 0.76, rad));
          float rim = stroke(rad - 0.68, 0.012);
          float ticks = pow(max(0.0, cos(angle * 12.0)), 16.0) * smoothstep(0.5, 0.54, rad) * (1.0 - smoothstep(0.61, 0.65, rad));
          float handAngle = floor(uTime * 2.0 + seed * 12.0) * 0.16;
          float hand = pow(max(0.0, cos(angle - handAngle)), 70.0) * (1.0 - smoothstep(0.45, 0.52, rad));
          vec3 dial = vec3(0.095, 0.05, 0.012) + vec3(1.0, 0.76, 0.37) * (rim * 0.45 + ticks * 0.65)
                    + vec3(1.0, 0.8, 0.42) * (hand * 0.65 + exp(-rad * rad * 170.0) * 0.18);
          inner += dial * on * min(max(glow, 0.8), 1.3);
        }

        // ---- glass shell
        vec3 N = normalize(vWorldN), V = normalize(cameraPosition - vWorld);
        float NdV = clamp(dot(N, V), 0.0, 1.0);
        float F = 0.04 + 0.96 * pow(1.0 - NdV, 4.0);
        vec3 R = reflect(-V, N);
        vec3 env = mix(vec3(0.02, 0.025, 0.035), vec3(0.4, 0.5, 0.65), smoothstep(-0.2, 0.9, R.y))
                 + vec3(1.0, 0.55, 0.25) * pow(max(0.0, dot(R, normalize(vec3(0.5, 0.1, -0.85)))), 8.0) * 0.6;
        float spec = pow(max(0.0, dot(R, uLightDir)), 160.0) * 1.3 + pow(max(0.0, dot(R, uLightDir)), 20.0) * 0.045;
        // old glass: faint surface wear catching the light
        float wear = n3(vObj * 18.0 + seed * 3.0);
        // etched rune band round the equator from grade III, glowing with the element
        float band = 0.0;
        if (grade >= 3.0) {
          float lat = vObj.y - 0.05 * sin(atan(vObj.z, vObj.x) * 2.0 + seed * 6.0);
          float a = atan(vObj.z, vObj.x) * (8.0 + grade * 2.0) / 6.2832 + t * 0.05;
          float glyph = step(0.35, h31(vec3(floor(a), floor(lat * 30.0), seed))) * step(abs(fract(a) - 0.5), 0.36);
          band = smoothstep(0.075, 0.06, abs(lat)) * smoothstep(0.02, 0.035, abs(lat)) * glyph
               + smoothstep(0.012, 0.0, abs(abs(lat) - 0.08)) * 0.8;
        }
        vec3 rim = mix(col, vec3(1.0), 0.08) * pow(1.0 - NdV, 3.0) * (0.22 + 0.24 * g01) * min(glow, 1.45);
        vec3 c = col * 0.025 + inner * (1.0 - F * 0.6)
               + env * F * (0.45 + wear * 0.35)
               + vec3(1.0, 0.97, 0.9) * spec
               + rim
               + mix(col, vec3(1.0), 0.12) * band * (0.4 + 0.2 * g01) * min(glow, 1.45)
               + mix(col, vec3(1.0), 0.12) * (min(flash, 3.0) * 0.32 + focus * 0.2) * (0.4 + 0.6 * pow(1.0 - NdV, 1.5));
        c = mix(c, vec3(0.008, 0.004, 0.013), hole * 0.96);
        gl_FragColor = vec4(c * fogKeep(), 1.0);
      }`,
  });
}
